import { TreeNode } from 'primeng/api'

import { NodeDefinition } from '@flowx/designer/modules/core/dictionary/process.dictionary'
import {
  NodeActionState,
  ProcessData,
  TokenDefinition,
} from '@flowx/designer/shared/entities/process-instance.types'

export function extractInitiatorNodeName(token: TokenDefinition, nodes: NodeDefinition[]): string {
  const node = nodes.find((node) => node.id === token.startNodeId)
  if (token.initiatorType === 'BOUNDARY_EVENT' && node) {
    return extractBoundaryEventInitiatorNodeName(token.initiatorId, node)
  }
  return node?.name || '-'
}

export function extractFirstExecutedNode(token: TokenDefinition): NodeActionState {
  const hasParentTokenId = !!token.parentTokenId
  const actionStatesLength = token.nodesActionStates.length

  if (!hasParentTokenId) {
    return token.nodesActionStates[0]
  }

  return actionStatesLength > 1 ? token.nodesActionStates[1] : token.nodesActionStates[0]
}

export function extractCurrentNode(token: TokenDefinition): NodeActionState {
  return token.nodesActionStates.find((node) => node.nodeId === token.currentNodeId)
}

export function buildTokenHierarchy(process: ProcessData): TreeNode[] {
  if (!process) {
    return []
  }
  const tokens = process.instance?.tokens
  if (!tokens.length) {
    return []
  }
  const nodes = process.definition?.nodeDefinitions || []

  const treeNodesMap = new Map<number, TreeNode>()
  const rootTreeNodes = []

  tokens.forEach((token) => {
    const context = !token.embedNodeId
      ? process.definition.resourceName
      : process.directEmbeddedSubprocessInstances?.find(
          (subProcess) => subProcess.embedNodeId === token.embedNodeId
        )?.processName
    const initiatorNodeName = extractInitiatorNodeName(token, nodes)
    const firstExecutedNode = extractFirstExecutedNode(token)?.name
    const currentNode = extractCurrentNode(token)?.name
    treeNodesMap.set(token.id, {
      data: { ...token, context, initiatorNode: initiatorNodeName, firstExecutedNode, currentNode },
      children: [],
      expanded: true,
    })
  })

  // Link children to parents
  for (const item of treeNodesMap.values()) {
    if (!item.data.parentTokenId) {
      rootTreeNodes.push(item)
    } else {
      const parent = treeNodesMap.get(item.data.parentTokenId)
      if (parent) {
        parent.children.push(item)
      } else {
        rootTreeNodes.push(item)
      }
    }
  }

  return rootTreeNodes
}

function extractBoundaryEventInitiatorNodeName(initiatorId: string, node: NodeDefinition): string {
  const messageEvent = node.messageEvents?.find((event) => event.id === Number(initiatorId))
  const timerEvent = node.timerEvents?.find((event) => event.id === Number(initiatorId))
  return messageEvent?.name || timerEvent?.name || node.name || '-'
}
